import { api } from "@/api/client";
import { convertToURLSearchParams } from "@/util";
import { Defect } from "../welds";

export type WeldListFilters = {
  ndtDiscipline?: string;
  operation?: string;
  subProjectId?: string;
  datasheetId?: string;
  onlyMyWelds: boolean;
  onlyRepairOrRewelds: boolean;
  onlyWeldsReadyForNdt: boolean;
};

export type WelderWeld = {
  weldId: string;
  weldNumber: string;
  weldMap: string;
  drawingNumber: string;
  jointType: string;
  wpsNumber: string;
  inspectionClass: string;
  competency: string;
  drawingId: string;
};

export type WelderRow = {
  weldId: string;
  passId: string;
  weldNumber: string;
  weldMap: string;
  drawingNumber: string;
  process: string;
  wpsNumber: string;
  filler: string;
  lotNumber: string;
  assetNumber: string;
  unfinished: boolean;
  finished: boolean;
  locked: boolean;
};

export type InspectorWeld = {
  weldId: string;
  weldNumber: string;
  weldMap: string;
  operation: string;
  drawingNumber: string;
  workInstruction: string;
  jointType: string;
  jointDetail: string;
  wpsNumber: string;
  rootGapDimensions: string;
  backingMaterialRequired: string;
  linearAlignment: string;
  acceptanceLevel: string;
  indicationAcceptanceLevel: string;
  technique: string;
};

export type InspectorRow = {
  weldId: string;
  weldNumber: string;
  weldMap: string;
  typeOfRepair: string;
  weldRepairWeld: boolean;
  weldRepairLevel: number;
  repairCount: number;
  drawingNumber: string;
  workInstruction: string;
  jointType: string;
  wpsNumber: string;
  rootGap: string;
  backingMaterial: string;
  linearAlignment: string;
  acceptanceLevel: string;
  indicationAcceptanceLevel: string;
  discipline: string;
  luxLevel: string;
  surfaceTemp: string;
  cleanerBatch: string;
  penetrantBatch: string;
  developerBatch: string;
  couplantBatch: string;
  surfaceCondition: string;
  equipmentNumber: string[];
  calibrationNumber: string[];
  accept?: boolean;
  defects: Defect[];
  probes: Probe[];
  attachment?: File;
  comment: string;
};

export type Probe = {
  hitachiProbeId?: string;
  serialNumber?: string;
  referenceDAC?: string;
  transferCorrection?: string;
  phaseAngle?: string;
  sensitivity?: string;
  calibrationBlocks?: string;
};

export type QualityPlanLocationResponse = {
  isAtDatasheetLevel: boolean;
};

export function getWelderOperations(parentId: string) {
  return api.get<string[]>(`hitachi/rbi/welder/operations/${parentId}`);
}

export function getWelderWeldList(filters: Partial<WeldListFilters>) {
  const params = convertToURLSearchParams(filters);
  return api.get<WelderWeld[]>(`hitachi/rbi/welder/welds?${params.toString()}`);
}

export function getWelderRows(weldIds: string[]) {
  return api.post<WelderRow[]>("hitachi/rbi/welder/processes", weldIds);
}

export function saveWelderRows(processes: WelderRow[]) {
  return api.post("hitachi/rbi/welder", processes);
}

export function getInspectorOperations(parentId: string, discipline: string) {
  return api.get<string[]>(`hitachi/rbi/inspector/operations/${parentId}?discipline=${discipline}`);
}

export function getInspectorWeldList(filters: Partial<WeldListFilters>) {
  const params = convertToURLSearchParams(filters);
  return api.get<InspectorWeld[]>(`hitachi/rbi/inspector/welds?${params.toString()}`);
}

export function getInspectorRows(discipline: string, weldIds: string[]) {
  return api.post<InspectorRow[]>(`hitachi/rbi/inspector/inspections?discipline=${discipline}`, weldIds);
}

export function saveInspectorRows(processes: InspectorRow[]) {
  return api.post("hitachi/rbi/inspector", processes);
}

export function getQualityPlanLocation(unitId: string) {
  return api.get<QualityPlanLocationResponse>(`hitachi/rbi/quality-plan/location/${unitId}`);
}
